import { useState, useCallback } from "react";
import StaffLayout from "@/components/StaffLayout";
import {
  useListWhatsChanged,
  useCreateWhatsChangedEntry,
  useUpdateWhatsChangedEntry,
  useDeleteWhatsChangedEntry,
  useReorderWhatsChangedEntries,
  useListCategories,
  getListWhatsChangedQueryKey,
  WhatsChangedEntry,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Edit, Trash2, GripVertical } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ICON_OPTIONS = [
  { value: "key-round", label: "Key (Login/Auth)" },
  { value: "truck", label: "Truck (UCR/Registration)" },
  { value: "file-text", label: "File (Documents/Filings)" },
  { value: "user-check", label: "User Check (BOC-3/Agents)" },
  { value: "refresh-cw", label: "Refresh (Updates/Renewals)" },
  { value: "search", label: "Search (Lookup)" },
  { value: "check-circle-2", label: "Check Circle (Reinstatement)" },
  { value: "globe", label: "Globe (Legacy/Portal)" },
];

const STATUS_OPTIONS = [
  { value: "improved", label: "Improved", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "changed", label: "Changed", className: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "watch", label: "Known Issues", className: "bg-amber-50 text-amber-700 border-amber-200" },
];

const entrySchema = z.object({
  categoryId: z.number().int().positive("Category is required"),
  icon: z.string().min(1, "Icon is required"),
  oldWay: z.string().min(1, "Old way description is required"),
  newWay: z.string().min(1, "New way description is required"),
  note: z.string().nullable().optional(),
  status: z.enum(["improved", "changed", "watch"]),
  sortOrder: z.number().int().default(0),
});

type EntryFormValues = z.infer<typeof entrySchema>;

const QUERY_KEY = getListWhatsChangedQueryKey();

function SortableRow({
  entry,
  onEdit,
  onDelete,
  statusBadge,
  categoryName,
}: {
  entry: WhatsChangedEntry;
  onEdit: (e: WhatsChangedEntry) => void;
  onDelete: (id: number) => void;
  statusBadge: (status: string) => React.ReactNode;
  categoryName: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? ("relative" as const) : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell>
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none p-1"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium text-slate-900 dark:text-white max-w-[180px]">
        {categoryName}
      </TableCell>
      <TableCell className="hidden md:table-cell text-xs text-slate-500 max-w-xs">
        <span className="line-clamp-2">{entry.oldWay}</span>
      </TableCell>
      <TableCell>{statusBadge(entry.status)}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(entry)}
          className="text-slate-500 hover:text-primary"
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(entry.id)}
          className="text-slate-500 hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default function StaffWhatsChangedPage() {
  const { data: serverEntries, isLoading } = useListWhatsChanged();
  const { data: categories } = useListCategories();
  const categoryById = new Map((categories ?? []).map((c) => [c.id, c]));
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createEntry = useCreateWhatsChangedEntry();
  const updateEntry = useUpdateWhatsChangedEntry();
  const deleteEntry = useDeleteWhatsChangedEntry();
  // Optimistic drag reorder. The query cache is the only source of truth for
  // the list. These callbacks live on the hook rather than on mutate(), so
  // they run for every request, even if the page unmounts before it settles.
  const reorderEntries = useReorderWhatsChangedEntries({
    mutation: {
      onMutate: async ({ data }) => {
        // Cancel any in-flight read first, or it can land after the
        // optimistic write and put the old order back.
        await queryClient.cancelQueries({ queryKey: QUERY_KEY });
        const snapshot = queryClient.getQueryData<WhatsChangedEntry[]>(QUERY_KEY);
        const sortOrderById = new Map(data.items.map((item) => [item.id, item.sortOrder]));
        queryClient.setQueryData<WhatsChangedEntry[]>(QUERY_KEY, (prev) =>
          prev
            ?.map((e) => ({ ...e, sortOrder: sortOrderById.get(e.id) ?? e.sortOrder }))
            .sort((a, b) => a.sortOrder - b.sortOrder)
        );
        return { snapshot };
      },
      onError: (_err, _vars, onMutateResult) => {
        toast({ title: "Failed to save order", variant: "destructive" });
        if (onMutateResult?.snapshot) {
          queryClient.setQueryData<WhatsChangedEntry[]>(QUERY_KEY, onMutateResult.snapshot);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      },
    },
  });

  const entries = serverEntries ?? [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const form = useForm<z.input<typeof entrySchema>, unknown, EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: {
      categoryId: 0,
      icon: "file-text",
      oldWay: "",
      newWay: "",
      note: "",
      status: "changed",
      sortOrder: 0,
    },
  });

  const openDialog = (entry?: WhatsChangedEntry) => {
    if (entry) {
      setEditingId(entry.id);
      form.reset({
        categoryId: entry.categoryId,
        icon: entry.icon,
        oldWay: entry.oldWay,
        newWay: entry.newWay,
        note: entry.note ?? "",
        status: entry.status as "improved" | "changed" | "watch",
        sortOrder: entry.sortOrder,
      });
    } else {
      setEditingId(null);
      form.reset({
        categoryId: 0,
        icon: "file-text",
        oldWay: "",
        newWay: "",
        note: "",
        status: "changed",
        sortOrder: entries.length,
      });
    }
    setIsDialogOpen(true);
  };

  const onSubmit = (data: EntryFormValues) => {
    const payload = {
      categoryId: data.categoryId,
      icon: data.icon,
      oldWay: data.oldWay,
      newWay: data.newWay,
      note: data.note || null,
      status: data.status,
      sortOrder: data.sortOrder,
    };

    if (editingId) {
      updateEntry.mutate(
        { id: editingId, data: payload },
        {
          onSuccess: () => {
            toast({ title: "Entry updated" });
            setIsDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          },
          onError: () => toast({ title: "Failed to update", variant: "destructive" }),
        }
      );
    } else {
      createEntry.mutate(
        { data: payload },
        {
          onSuccess: () => {
            toast({ title: "Entry created" });
            setIsDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          },
          onError: () => toast({ title: "Failed to create", variant: "destructive" }),
        }
      );
    }
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this entry? It will be removed from the public page.")) return;
    deleteEntry.mutate(
      { id },
      {
        onSuccess: () => {
          toast({ title: "Entry deleted" });
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
      }
    );
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      // One reorder at a time: a second drop while the first is saving would
      // take its snapshot from an order the server has not confirmed yet.
      if (reorderEntries.isPending) return;

      const currentEntries = serverEntries ?? [];
      const oldIndex = currentEntries.findIndex((e) => e.id === active.id);
      const newIndex = currentEntries.findIndex((e) => e.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(currentEntries, oldIndex, newIndex).map(
        (e, i) => ({ ...e, sortOrder: i })
      );

      reorderEntries.mutate({
        data: {
          items: reordered.map((e) => ({ id: e.id, sortOrder: e.sortOrder })),
        },
      });
    },
    [serverEntries, reorderEntries]
  );

  const statusBadge = (status: string) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status) ?? STATUS_OPTIONS[1];
    return (
      <Badge variant="outline" className={`text-xs ${opt.className}`}>
        {opt.label}
      </Badge>
    );
  };

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            What's Changed Entries
          </h1>
          <p className="text-slate-500 text-sm">
            Manage the entries shown on the public page. Drag rows to reorder them.
          </p>
        </div>
        <Button onClick={() => openDialog()} className="bg-primary hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="border border-slate-200 rounded-xl bg-white dark:bg-slate-900 overflow-hidden shadow-sm">
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="hidden md:table-cell">Old Way (preview)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={entries.map((e) => e.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-slate-500">
                        No entries yet. Click "New Entry" to add one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry) => (
                      <SortableRow
                        key={entry.id}
                        entry={entry}
                        onEdit={openDialog}
                        onDelete={handleDelete}
                        statusBadge={statusBadge}
                        categoryName={categoryById.get(entry.categoryId)?.name ?? "—"}
                      />
                    ))
                  )}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Entry" : "New Entry"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem className="col-span-2 sm:col-span-1">
                      <FormLabel>Category</FormLabel>
                      <Select
                        value={field.value ? String(field.value) : ""}
                        onValueChange={(val) => field.onChange(Number(val))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(categories ?? []).filter((c) => c.isActive).map((cat) => (
                            <SelectItem key={cat.id} value={String(cat.id)}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem className="col-span-2 sm:col-span-1">
                      <FormLabel>Icon</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ICON_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="oldWay"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Old Way</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="How this worked before MOTUS..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newWay"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>MOTUS (New Way)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="How it works now in MOTUS..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Note{" "}
                      <span className="text-slate-400 font-normal text-xs">
                        (optional — shown as amber warning strip)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="e.g. A known bug is affecting this area — tracked on the Known Issues board."
                        className="min-h-[60px]"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-orange-600"
                  disabled={createEntry.isPending || updateEntry.isPending}
                >
                  {editingId ? "Save Changes" : "Create Entry"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </StaffLayout>
  );
}
